/**
 * Archetype Generators — Melee-inspired stage archetypes.
 *
 * Each archetype produces a WireframeLayout: ground segments and floating
 * platforms. The layout is rasterized to the grid as CELL.WIREFRAME, then
 * draped with sand. Platforms float freely (like Melee) — no vertical
 * beams connecting them, so movement flows naturally between tiers.
 *
 * 3 archetypes:
 *   Battlefield — High-variability ground (1-3 segments, gaps, bridges) + 2-6 platforms of varied sizes
 *   Archipelago — Disconnected islands with optional horizontal bridges
 *   Tower       — Stacked narrowing platforms
 */

import { WORLD, ARCHETYPE, ASCENT } from '../constants';
import { random, randomRange, randomInt, randomChance } from './rng';
import type { WireframeLayout, WireframeNode, WireframeBeam, WireframePlatform, WireframeGround } from '../types';

// --- Types ---

export type ArchetypeType = 'battlefield' | 'archipelago' | 'tower' | 'ascent';

export interface ArchetypeDef {
  type: ArchetypeType;
  displayName: string;
  weight: number;
}

const ARCHETYPES: ArchetypeDef[] = [
  { type: 'battlefield',  displayName: 'Battlefield',  weight: ARCHETYPE.WEIGHTS.BATTLEFIELD },
  { type: 'archipelago',  displayName: 'Archipelago',  weight: ARCHETYPE.WEIGHTS.ARCHIPELAGO },
  { type: 'tower',        displayName: 'Tower',         weight: ARCHETYPE.WEIGHTS.TOWER },
];

// --- Selection ---

/** Weighted random archetype selection using module RNG. */
export function selectArchetype(): ArchetypeDef {
  const totalWeight = ARCHETYPES.reduce((sum, a) => sum + a.weight, 0);
  let roll = random() * totalWeight;
  for (const arch of ARCHETYPES) {
    roll -= arch.weight;
    if (roll <= 0) return arch;
  }
  return ARCHETYPES[0];
}

// --- Helpers ---

/** Weighted random pick from [value, weight] pairs using module RNG. */
function weightedPick<T>(choices: [T, number][]): T {
  const total = choices.reduce((sum, [, w]) => sum + w, 0);
  let roll = random() * total;
  for (const [value, weight] of choices) {
    roll -= weight;
    if (roll <= 0) return value;
  }
  return choices[0][0];
}

// --- Layout generation ---

export function generateArchetypeLayout(
  type: ArchetypeType,
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  switch (type) {
    case 'battlefield':  return generateBattlefield(stageLeft, stageWidth);
    case 'archipelago':  return generateArchipelago(stageLeft, stageWidth);
    case 'tower':        return generateTower(stageLeft, stageWidth);
    case 'ascent':       return generateAscent(stageLeft, stageWidth);
  }
}

// --- Battlefield ---
//
// High-variability archetype: ground topology ranges from single full-span
// to multi-segment with gaps (void, bridge, or gap-fill platform for LOS
// blocking). 2-6 platforms of varied sizes across 3 height bands.

type GroundTopology = 'full_span' | 'two_segment' | 'three_segment' | 'asymmetric';

function generateBattlefield(stageLeft: number, stageWidth: number): WireframeLayout {
  const canvasH = WORLD.VIRTUAL_HEIGHT;
  const baseGroundY = canvasH * randomRange(ARCHETYPE.GROUND_Y_FRAC[0], ARCHETYPE.GROUND_Y_FRAC[1]);
  const groundDepth = ARCHETYPE.GROUND_DEPTH;
  const platThick = ARCHETYPE.PLATFORM_THICKNESS;

  const groundSegments: WireframeGround[] = [];
  const nodes: WireframeNode[] = [];
  const beams: WireframeBeam[] = [];
  const platforms: WireframePlatform[] = [];

  // --- Ground topology ---
  const topology = weightedPick<GroundTopology>([
    ['full_span',     0.25],
    ['two_segment',   0.40],
    ['three_segment', 0.25],
    ['asymmetric',    0.10],
  ]);

  // Track gaps for gap treatment
  interface GapInfo { x: number; width: number; leftY: number; rightY: number; }
  const gaps: GapInfo[] = [];

  switch (topology) {
    case 'full_span': {
      groundSegments.push({
        x: stageLeft,
        width: stageWidth,
        surfaceY: baseGroundY,
        depth: groundDepth,
      });
      break;
    }
    case 'two_segment': {
      const gapWidth = randomRange(180, 400);
      const gapCenter = stageLeft + stageWidth * randomRange(0.35, 0.65);
      const gapLeft = gapCenter - gapWidth / 2;
      const gapRight = gapCenter + gapWidth / 2;

      const leftY = baseGroundY + randomRange(-25, 25);
      const rightY = baseGroundY + randomRange(-25, 25);

      groundSegments.push(
        { x: stageLeft, width: gapLeft - stageLeft, surfaceY: leftY, depth: groundDepth },
        { x: gapRight, width: (stageLeft + stageWidth) - gapRight, surfaceY: rightY, depth: groundDepth },
      );
      gaps.push({ x: gapLeft, width: gapWidth, leftY, rightY });
      break;
    }
    case 'three_segment': {
      const totalGapWidth = randomRange(300, 600);
      // Split gap unevenly
      const gap1Frac = randomRange(0.35, 0.65);
      const gap1Width = totalGapWidth * gap1Frac;
      const gap2Width = totalGapWidth * (1 - gap1Frac);

      // Segment widths: unequal, 25-40% each for first two, remainder for third
      const usable = stageWidth - totalGapWidth;
      const seg1Frac = randomRange(0.25, 0.40);
      const seg2Frac = randomRange(0.25, 0.40);
      const seg3Frac = 1 - seg1Frac - seg2Frac;
      const seg1W = Math.max(200, usable * seg1Frac);
      const seg2W = Math.max(200, usable * seg2Frac);
      const seg3W = Math.max(200, usable * seg3Frac);

      const y1 = baseGroundY + randomRange(-30, 30);
      const y2 = baseGroundY + randomRange(-30, 30);
      const y3 = baseGroundY + randomRange(-30, 30);

      const seg1Left = stageLeft;
      const gap1Left = seg1Left + seg1W;
      const seg2Left = gap1Left + gap1Width;
      const gap2Left = seg2Left + seg2W;
      const seg3Left = gap2Left + gap2Width;

      groundSegments.push(
        { x: seg1Left, width: seg1W, surfaceY: y1, depth: groundDepth },
        { x: seg2Left, width: seg2W, surfaceY: y2, depth: groundDepth },
        { x: seg3Left, width: seg3W, surfaceY: y3, depth: groundDepth },
      );
      gaps.push(
        { x: gap1Left, width: gap1Width, leftY: y1, rightY: y2 },
        { x: gap2Left, width: gap2Width, leftY: y2, rightY: y3 },
      );
      break;
    }
    case 'asymmetric': {
      const largeFrac = randomRange(0.55, 0.70);
      const smallFrac = randomRange(0.15, 0.25);
      const gapFrac = 1 - largeFrac - smallFrac;
      const largeW = stageWidth * largeFrac;
      const smallW = stageWidth * smallFrac;
      const gapW = stageWidth * gapFrac;

      const largeY = baseGroundY;
      const smallY = baseGroundY - randomRange(40, 80); // elevated perch

      // Random left/right placement
      if (randomChance(0.5)) {
        // Large left, small right
        groundSegments.push(
          { x: stageLeft, width: largeW, surfaceY: largeY, depth: groundDepth },
          { x: stageLeft + largeW + gapW, width: smallW, surfaceY: smallY, depth: groundDepth },
        );
        gaps.push({ x: stageLeft + largeW, width: gapW, leftY: largeY, rightY: smallY });
      } else {
        // Small left, large right
        groundSegments.push(
          { x: stageLeft, width: smallW, surfaceY: smallY, depth: groundDepth },
          { x: stageLeft + smallW + gapW, width: largeW, surfaceY: largeY, depth: groundDepth },
        );
        gaps.push({ x: stageLeft + smallW, width: gapW, leftY: smallY, rightY: largeY });
      }
      break;
    }
  }

  // --- Gap treatment ---
  for (const gap of gaps) {
    const treatment = weightedPick<'fill' | 'bridge' | 'open'>([
      ['fill',   0.55],
      ['bridge', 0.25],
      ['open',   0.20],
    ]);

    if (treatment === 'fill') {
      // Gap-fill platform — primary LOS blocker
      const fillFrac = randomRange(0.35, 0.65);
      const fillWidth = gap.width * fillFrac;
      const fillX = gap.x + (gap.width - fillWidth) / 2;
      const avgGroundY = (gap.leftY + gap.rightY) / 2;
      const fillY = avgGroundY - randomRange(0, 100);

      platforms.push({
        x: fillX,
        y: fillY,
        width: fillWidth,
        thickness: platThick,
      });
    } else if (treatment === 'bridge') {
      // Bridge beam — thin, walkable
      const avgGroundY = (gap.leftY + gap.rightY) / 2;
      const bridgeY = avgGroundY - randomRange(10, 50);
      const nodeLeft = nodes.length;
      const nodeRight = nodes.length + 1;
      nodes.push(
        { x: gap.x, y: bridgeY, radius: ARCHETYPE.NODE_RADIUS },
        { x: gap.x + gap.width, y: bridgeY, radius: ARCHETYPE.NODE_RADIUS },
      );
      beams.push({ from: nodeLeft, to: nodeRight, thickness: ARCHETYPE.BEAM_THICKNESS + 1 });
    }
    // 'open' — no treatment, void gap
  }

  // --- Platform generation ---
  // Reference ground Y for height bands (average of all segments)
  const avgGroundY = groundSegments.reduce((sum, s) => sum + s.surfaceY, 0) / groundSegments.length;

  // Height bands relative to ground — 4 bands for better vertical spread
  type HeightBand = 'low' | 'mid' | 'high' | 'sky';
  const bandRanges: Record<HeightBand, [number, number]> = {
    low:  [150, 300],
    mid:  [300, 500],
    high: [500, 700],
    sky:  [700, 900],
  };

  // Platform size classes
  type SizeClass = 'perch' | 'small' | 'medium' | 'large';
  const sizeWidths: Record<SizeClass, [number, number]> = {
    perch:  [30, 60],
    small:  [60, 100],
    medium: [100, 175],
    large:  [175, 250],
  };

  // Target: 4-8 total platforms including gap-fills
  const targetTotal = randomInt(2, 4);

  // Guarantee at least 1 platform per band, then fill remaining randomly
  const bandOrder: HeightBand[] = ['low', 'mid', 'high', 'sky'];

  // Helper: try to place a platform in a band
  const tryPlace = (band: HeightBand): boolean => {
    const sizeClass = weightedPick<SizeClass>([
      ['perch',  0.25],
      ['small',  0.30],
      ['medium', 0.30],
      ['large',  0.15],
    ]);

    const [minW, maxW] = sizeWidths[sizeClass];
    const platW = randomRange(minW, maxW);
    const [minH, maxH] = bandRanges[band];
    const platY = avgGroundY - randomRange(minH, maxH);
    const platX = stageLeft + randomRange(0, Math.max(1, stageWidth - platW));

    if (platY < 100) return false;
    if (platX < stageLeft || platX + platW > stageLeft + stageWidth) return false;

    for (const existing of platforms) {
      const hOverlap = platX < existing.x + existing.width && platX + platW > existing.x;
      if (hOverlap) {
        if (Math.abs(platY - existing.y) < 120) return false;
      } else {
        const hGap = Math.max(0,
          Math.max(platX, existing.x) - Math.min(platX + platW, existing.x + existing.width)
        );
        if (hGap < 80) return false;
      }
    }

    for (const seg of groundSegments) {
      const hOverlap = platX < seg.x + seg.width && platX + platW > seg.x;
      if (hOverlap && seg.surfaceY - platY < 60) return false;
    }

    platforms.push({ x: platX, y: platY, width: platW, thickness: platThick });
    return true;
  };

  // Pass 1: guarantee 1 per band (3 attempts each)
  for (const band of bandOrder) {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (tryPlace(band)) break;
    }
  }

  // Pass 2: fill up to target, cycling through bands
  let bandIdx = 0;
  let failStreak = 0;
  while (platforms.length < targetTotal && failStreak < 12) {
    const band = bandOrder[bandIdx % bandOrder.length];
    bandIdx++;
    if (tryPlace(band)) { failStreak = 0; } else { failStreak++; }
  }

  return { nodes, beams, platforms, groundSegments };
}

// --- Archipelago ---
//
// ═══════    ┌──┐    ═══════   (islands + floating platform)
//          ──────────          (horizontal bridge beam)
//          ══════════          (island)

function generateArchipelago(stageLeft: number, stageWidth: number): WireframeLayout {
  const canvasH = WORLD.VIRTUAL_HEIGHT;
  const baseGroundY = canvasH * randomRange(ARCHETYPE.GROUND_Y_FRAC[0], ARCHETYPE.GROUND_Y_FRAC[1]);
  const groundDepth = ARCHETYPE.GROUND_DEPTH;

  const numIslands = randomInt(2, 4);
  const gapWidth = randomRange(150, 300);
  const totalGapSpace = gapWidth * (numIslands - 1);
  const usableWidth = stageWidth - totalGapSpace;
  const baseIslandWidth = usableWidth / numIslands;

  const groundSegments: WireframeGround[] = [];
  const nodes: WireframeNode[] = [];
  const beams: WireframeBeam[] = [];
  const platforms: WireframePlatform[] = [];

  let currentX = stageLeft;

  for (let i = 0; i < numIslands; i++) {
    const islandWidth = baseIslandWidth * randomRange(0.75, 1.25);
    const islandY = baseGroundY + randomRange(-100, 100);

    groundSegments.push({
      x: currentX,
      width: islandWidth,
      surfaceY: islandY,
      depth: groundDepth,
    });

    // 35% chance of a floating platform above each island
    if (randomChance(0.35)) {
      const platWidth = islandWidth * randomRange(0.2, 0.35);
      const platY = islandY - randomRange(150, 280);
      const platX = currentX + (islandWidth - platWidth) / 2 + randomRange(-30, 30);
      const clampedX = Math.max(stageLeft, Math.min(stageLeft + stageWidth - platWidth, platX));

      if (platY > 100) {
        platforms.push({
          x: clampedX,
          y: platY,
          width: platWidth,
          thickness: ARCHETYPE.PLATFORM_THICKNESS,
        });
      }
    }

    currentX += islandWidth;

    // Horizontal bridge beam between adjacent islands (50% chance per gap)
    if (i < numIslands - 1) {
      const bridgeStartX = currentX;
      currentX += gapWidth;

      if (randomChance(0.5)) {
        const bridgeY = baseGroundY - randomRange(40, 100);
        const bridgeNodeLeft = nodes.length;
        const bridgeNodeRight = nodes.length + 1;
        nodes.push(
          { x: bridgeStartX, y: bridgeY, radius: ARCHETYPE.NODE_RADIUS },
          { x: currentX, y: bridgeY, radius: ARCHETYPE.NODE_RADIUS },
        );
        beams.push({ from: bridgeNodeLeft, to: bridgeNodeRight, thickness: ARCHETYPE.BEAM_THICKNESS + 1 });
      }
    }
  }

  // High spanning platform (20% chance) — uses vertical space above islands
  if (randomChance(0.20)) {
    const skyW = randomRange(60, 125);
    const skyY = baseGroundY - randomRange(400, 650);
    const skyX = stageLeft + randomRange(100, Math.max(101, stageWidth - skyW - 100));
    if (skyY > 100) {
      platforms.push({ x: skyX, y: skyY, width: skyW, thickness: ARCHETYPE.PLATFORM_THICKNESS });
    }
  }

  return { nodes, beams, platforms, groundSegments };
}

// --- Tower ---
//
//      ┌────┐           (top, narrow, floating)
//
//   ┌──────────┐        (mid, medium, floating)
//
// ════════════════════   (ground, wide)

function generateTower(stageLeft: number, stageWidth: number): WireframeLayout {
  const canvasH = WORLD.VIRTUAL_HEIGHT;
  const groundY = canvasH * randomRange(ARCHETYPE.GROUND_Y_FRAC[0], ARCHETYPE.GROUND_Y_FRAC[1]);
  const groundDepth = ARCHETYPE.GROUND_DEPTH;
  const cx = stageLeft + stageWidth / 2;

  const groundSegments: WireframeGround[] = [{
    x: stageLeft,
    width: stageWidth,
    surfaceY: groundY,
    depth: groundDepth,
  }];

  const numTiers = randomInt(1, 3);
  const tierSpacing = randomRange(200, 280);
  const shrinkFactor = randomRange(0.55, 0.70);

  const platforms: WireframePlatform[] = [];
  const platThick = ARCHETYPE.PLATFORM_THICKNESS;

  // Spiral variant: tiers zigzag left/right instead of centering
  const spiral = randomChance(0.30);
  let spiralDir = randomChance(0.5) ? 1 : -1;

  let prevWidth = stageWidth;
  let prevY = groundY;

  for (let tier = 0; tier < numTiers; tier++) {
    const tierWidth = prevWidth * shrinkFactor;
    const tierY = prevY - tierSpacing;
    if (tierY < 100) break;

    // Offset from center — spiral alternates, normal is random
    let offsetX = 0;
    if (spiral) {
      offsetX = spiralDir * tierWidth * randomRange(0.10, 0.20);
      spiralDir *= -1;
    } else {
      offsetX = randomRange(-tierWidth * 0.15, tierWidth * 0.15);
    }
    const tierLeftX = Math.max(stageLeft, Math.min(
      stageLeft + stageWidth - tierWidth,
      cx + offsetX - tierWidth / 2,
    ));

    platforms.push({
      x: tierLeftX,
      y: tierY,
      width: tierWidth,
      thickness: platThick,
    });

    // Flanking perch — small platform off to one side (30% per tier)
    if (randomChance(0.30)) {
      const perchW = randomRange(30, 70);
      const side = randomChance(0.5) ? -1 : 1;
      const perchX = side < 0
        ? tierLeftX - perchW - randomRange(30, 80)
        : tierLeftX + tierWidth + randomRange(30, 80);
      if (perchX >= stageLeft && perchX + perchW <= stageLeft + stageWidth && tierY > 100) {
        platforms.push({ x: perchX, y: tierY, width: perchW, thickness: platThick });
      }
    }

    prevWidth = tierWidth;
    prevY = tierY;
  }

  return { nodes: [], beams: [], platforms, groundSegments };
}

// --- Ascent ---
//
// Vertical survival: 15-20 tiers of ground segments stacked up an 8000px tall world.
// Bottom tier is wide (spawn platform). Top tiers are narrow. Players climb to escape
// the rising void. All segments are WireframeGround so getGroundY() works for tank placement.

function generateAscent(stageLeft: number, stageWidth: number): WireframeLayout {
  const worldH = WORLD.VIRTUAL_HEIGHT; // 8000 in ascent mode
  const groundDepth = ARCHETYPE.GROUND_DEPTH;
  const cx = stageLeft + stageWidth / 2;

  const groundSegments: WireframeGround[] = [];

  // Number of tiers
  const [tierMin, tierMax] = ASCENT.TIER_COUNT;
  const numTiers = randomInt(tierMin, tierMax);

  // Distribute tiers across vertical space (bottom = high Y, top = low Y)
  // Bottom tier near Y=7600, top tier near Y=400
  const bottomY = worldH - 400;  // leave space for void start
  const topY = 400;              // leave space for ceiling blast zone

  for (let tier = 0; tier < numTiers; tier++) {
    // Progress 0 (bottom) to 1 (top)
    const t = tier / (numTiers - 1);
    // Add spacing jitter
    const [spacingMin, spacingMax] = ASCENT.TIER_SPACING;
    const baseY = bottomY - t * (bottomY - topY);
    const jitter = randomRange(-spacingMin * 0.3, spacingMax * 0.3);
    const tierY = Math.max(topY, Math.min(bottomY, baseY + jitter));

    // Width narrows toward top
    const widthFrac = ASCENT.BOTTOM_TIER_WIDTH_FRAC + (ASCENT.TOP_TIER_WIDTH_FRAC - ASCENT.BOTTOM_TIER_WIDTH_FRAC) * t;
    const tierWidth = stageWidth * widthFrac;

    // Platforms per tier
    const [platMin, platMax] = ASCENT.PLATFORMS_PER_TIER;

    // Bottom tier: always 1 wide platform for spawning
    if (tier === 0) {
      groundSegments.push({
        x: cx - tierWidth / 2,
        width: tierWidth,
        surfaceY: tierY,
        depth: groundDepth,
      });
      continue;
    }

    // Special tier types
    if (randomChance(ASCENT.BRIDGE_TIER_CHANCE)) {
      // Bridge tier: one wide platform spanning most of the width
      groundSegments.push({
        x: cx - tierWidth / 2,
        width: tierWidth,
        surfaceY: tierY,
        depth: groundDepth,
      });
      continue;
    }

    if (randomChance(ASCENT.GAP_TIER_CHANCE)) {
      // Gap tier: small scattered platforms
      const numSmall = randomInt(2, 3);
      const smallWidth = tierWidth / (numSmall * 2);
      for (let i = 0; i < numSmall; i++) {
        const segX = stageLeft + randomRange(0, stageWidth - smallWidth);
        groundSegments.push({
          x: segX,
          width: smallWidth,
          surfaceY: tierY + randomRange(-15, 15),
          depth: groundDepth,
        });
      }
      continue;
    }

    // Normal tier: 1-3 platforms
    const numPlatforms = randomInt(platMin, platMax);
    const [platWidthMin, platWidthMax] = ASCENT.PLATFORM_WIDTH;

    if (numPlatforms === 1) {
      // Single platform, centered with offset
      const w = randomRange(platWidthMin, Math.min(platWidthMax, tierWidth));
      const offsetX = randomRange(-tierWidth * 0.2, tierWidth * 0.2);
      groundSegments.push({
        x: cx + offsetX - w / 2,
        width: w,
        surfaceY: tierY,
        depth: groundDepth,
      });
    } else {
      // Multiple platforms distributed across the tier width
      const usable = stageWidth - 100; // margin on each side
      const segWidth = usable / numPlatforms;
      for (let i = 0; i < numPlatforms; i++) {
        const w = randomRange(platWidthMin, Math.min(platWidthMax, segWidth * 0.8));
        const segCenterX = stageLeft + 50 + segWidth * (i + 0.5);
        groundSegments.push({
          x: segCenterX - w / 2,
          width: w,
          surfaceY: tierY + randomRange(-15, 15),
          depth: groundDepth,
        });
      }
    }
  }

  return { nodes: [], beams: [], platforms: [], groundSegments };
}
